import { CoreEnums } from '@/utils/domain/constants';

const { ReportProvider, ReportType, ReportDataFilterDimension } = CoreEnums;

const unwrapScalar = (value) =>
  value && typeof value === 'object' ? value.value ?? value.id ?? value.key ?? value : value;

export const isNativeProvider = (value) => {
  const raw = unwrapScalar(value);
  return raw === ReportProvider.Native || raw === 'native' || raw === 1 || raw === '1';
};

export const isPowerBIProvider = (value) => !isNativeProvider(value);

export const normalizeReportProvider = (value) => {
  const raw = unwrapScalar(value);
  return raw == null || raw === ''
    ? undefined
    : isNativeProvider(raw)
    ? ReportProvider.Native
    : ReportProvider.PowerBI;
};

export const normalizeReportType = (value) => {
  const raw = unwrapScalar(value);
  if (raw == null || raw === '') return undefined;
  const asNumber = Number(raw);
  if (asNumber === ReportType.Administration || raw === 'Administration') {
    return ReportType.Administration;
  }
  if (asNumber === ReportType.Process || raw === 'Process') {
    return ReportType.Process;
  }
  if (asNumber === ReportType.Request || raw === 'Request') {
    return ReportType.Request;
  }
  if (asNumber === ReportType.Generic || raw === 'Generic') {
    return ReportType.Generic;
  }
  return raw;
};

export const unwrapNativeReportConfig = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const nested = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  if (
    nested.code ||
    nested.datasets ||
    nested.queries ||
    nested.filters ||
    nested.parameters ||
    nested.settings
  ) {
    return nested;
  }
  if (nested.configurations && typeof nested.configurations === 'object') {
    return nested.configurations;
  }
  return nested;
};

export const normalizeReportTokenSession = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const src =
    raw.data && typeof raw.data === 'object' && (raw.data.token || raw.data.versionId || raw.data.accessToken)
      ? raw.data
      : raw;
  const nestedToken = src.token && typeof src.token === 'object' ? src.token : null;
  const token =
    (typeof src.token === 'string' && src.token) ||
    nestedToken?.token ||
    src.accessToken ||
    src.value;
  const versionId = src.versionId || src.version?.id || nestedToken?.versionId;
  return {
    ...src,
    token,
    versionId,
    versionNumber: src.versionNumber || src.version?.number || nestedToken?.versionNumber,
    reportId: src.reportId || nestedToken?.reportId,
  };
};

export const unwrapEntityId = (value) => {
  if (value == null || value === '') return undefined;
  if (Array.isArray(value)) return unwrapEntityId(value[0]);
  if (typeof value === 'object') return value.id ?? value.value ?? value.key ?? undefined;
  return value;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const isEmptyGuid = (value) => {
  const id = unwrapEntityId(value);
  return !id || String(id).toLowerCase() === EMPTY_GUID;
};

export const toAccessControlList = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      if (!row) return null;
      const id = row.id ?? row.userId ?? row.key;
      if (!id) return null;
      return {
        id,
        name: row.name ?? row.fullName ?? row.label ?? '',
        ...(row.email ? { email: row.email } : {}),
      };
    })
    .filter(Boolean);
};

export const flattenAccessControl = (accessControl) => {
  const ac = accessControl && typeof accessControl === 'object' ? accessControl : {};
  return {
    users: ac.users || [],
    userGroups: ac.userGroups || [],
    positions: ac.positions || [],
    areas: ac.areas || [],
    filtersLogicalOperator: ac.filtersLogicalOperator || 'or',
    userGroupsFiltersLogicalOperator: ac.userGroupsFiltersLogicalOperator || 'or',
  };
};

export const buildAccessControl = (values = {}) => ({
  users: toAccessControlList(values.users),
  userGroups: toAccessControlList(values.userGroups),
  positions: toAccessControlList(values.positions),
  areas: toAccessControlList(values.areas),
  filtersLogicalOperator: values.filtersLogicalOperator || 'or',
  userGroupsFiltersLogicalOperator: values.userGroupsFiltersLogicalOperator || 'or',
});

const ACCESS_CONTROL_INPUT_KEYS = [
  'users',
  'userGroups',
  'positions',
  'areas',
  'filtersLogicalOperator',
  'userGroupsFiltersLogicalOperator',
  'accessControl',
];

export const hasAccessControlInput = (values = {}) =>
  ACCESS_CONTROL_INPUT_KEYS.some((key) => values[key] !== undefined);

export const unwrapAllowedList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

export const flattenAllowedSubjects = ({ users, positions, areas, userGroups } = {}) => ({
  users: toAccessControlList(users),
  positions: toAccessControlList(positions),
  areas: toAccessControlList(areas),
  userGroups: toAccessControlList(userGroups),
});

/** Live consume editor body. Operators are not on this DTO. */
export const buildUpdatePermissionParameters = (values = {}) => ({
  users: toAccessControlList(values.users).map((row) => ({
    id: row.id,
    fullName: row.name || '',
    ...(row.email ? { email: row.email } : {}),
  })),
  positions: toAccessControlList(values.positions).map((row) => ({
    id: row.id,
    name: row.name || '',
  })),
  userGroups: toAccessControlList(values.userGroups).map((row) => ({
    id: row.id,
    name: row.name || '',
  })),
  areas: toAccessControlList(values.areas).map((row) => ({
    id: row.id,
    name: row.name || '',
  })),
});

const DIMENSIONS = [
  {
    dimension: ReportDataFilterDimension.User,
    enabled: 'isReportDataFilterByUserEnabled',
    table: 'reportDataFilterUserTableName',
    field: 'reportDataFilterUserFieldName',
    fieldInternal: 'reportDataFilterUserFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.Position,
    enabled: 'isReportDataFilterByPositionEnabled',
    table: 'reportDataFilterPositionTableName',
    field: 'reportDataFilterPositionFieldName',
    fieldInternal: 'reportDataFilterPositionFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.UserCompany,
    enabled: 'isReportDataFilterByUserCompanyEnabled',
    table: 'reportDataFilterUserCompanyTableName',
    field: 'reportDataFilterUserCompanyFieldName',
    fieldInternal: 'reportDataFilterUserCompanyFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.UserEnvironment,
    enabled: 'isReportDataFilterByUserEnvironmentEnabled',
    table: 'reportDataFilterUserEnvironmentTableName',
    field: 'reportDataFilterUserEnvironmentFieldName',
    fieldInternal: 'reportDataFilterUserEnvironmentFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.Area,
    enabled: 'isReportDataFilterByAreaEnabled',
    table: 'reportDataFilterAreaTableName',
    field: 'reportDataFilterAreaFieldName',
    fieldInternal: 'reportDataFilterAreaFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.UserGroup,
    enabled: 'isReportDataFilterByUserGroupsEnabled',
    table: 'reportDataFilterUserGroupTableName',
    field: 'reportDataFilterUserGroupFieldName',
    fieldInternal: 'reportDataFilterUserGroupFieldNameInternal',
  },
  {
    dimension: ReportDataFilterDimension.DynamicEntity,
    enabled: 'isReportDataFilterByDynamicEntityEnabled',
    table: 'reportDataFilterDynamicEntityTableName',
    field: 'reportDataFilterDynamicEntityFieldName',
    extra: (values) => ({
      dynamicEntityId: unwrapEntityId(values.reportDataFilterDynamicEntityId),
      dynamicEntityFieldUserFieldId: unwrapEntityId(
        values.reportDataFilterDynamicEntityFieldUserFieldId,
      ),
      dynamicEntityFieldDataFieldId: unwrapEntityId(
        values.reportDataFilterDynamicEntityFieldDataFieldId,
      ),
    }),
  },
];

export const buildConfigPowerBIReport = (values = {}) => {
  const dataFilters = DIMENSIONS.filter((dim) => values[dim.enabled]).map((dim) => ({
    dimension: dim.dimension,
    table: values[dim.table] || undefined,
    field: values[dim.field] || undefined,
    fieldInternal: values[dim.fieldInternal] || undefined,
    ...(dim.extra ? dim.extra(values) : {}),
  }));

  const selectedGroup = values.reportGroup || values.reportGroupId;
  const groupWorkspace =
    values.workspaceId ||
    values.powerBIReportGroupId ||
    (selectedGroup && typeof selectedGroup === 'object'
      ? selectedGroup.powerBIReportGroupId || selectedGroup.powerBIReportGroupID
      : undefined);

  return {
    provider: ReportProvider.PowerBI,
    reportId: values.powerBIReportId || values.reportId || undefined,
    datasetId: values.powerBIDatasetId || values.datasetId || undefined,
    workspaceId: groupWorkspace || undefined,
    powerBIServerConfigurationId: unwrapEntityId(values.powerBIServerConfigurationId),
    defaultReportSection: values.powerBIDefaultReportSection || values.defaultReportSection,
    isMultipage: !!values.isMultipage,
    dataFilters,
  };
};

const matchDataFilterDimension = (filterDimension) => {
  const token = unwrapScalar(filterDimension);
  return DIMENSIONS.find(
    (dim) => dim.dimension === token || dim.dimension === filterDimension || dim.dimension === String(token),
  );
};

export const applyPowerBIConfigToForm = (item = {}, config = {}) => {
  const next = { ...item };
  if (config.reportId != null && config.reportId !== '') {
    next.powerBIReportId = config.reportId;
  }
  if (config.datasetId != null && config.datasetId !== '') {
    next.powerBIDatasetId = config.datasetId;
  }
  if (config.defaultReportSection != null && config.defaultReportSection !== '') {
    next.powerBIDefaultReportSection = config.defaultReportSection;
  }
  if (typeof config.isMultipage === 'boolean') {
    next.isMultipage = config.isMultipage;
  }

  const server = config.serverConfiguration || item.powerBIServerConfiguration;
  const serverId =
    unwrapEntityId(server) ||
    unwrapEntityId(config.powerBIServerConfigurationId) ||
    unwrapEntityId(item.powerBIServerConfigurationId);
  if (serverId) {
    next.powerBIServerConfigurationId = serverId;
  }
  if (server && typeof server === 'object') {
    next.powerBIServerConfiguration = {
      id: server.id || serverId,
      name: server.name,
    };
  }
  delete next.serverConfiguration;

  if (config.workspaceId != null && config.workspaceId !== '') {
    next.workspaceId = config.workspaceId;
  } else if (!next.workspaceId && (item.reportGroup?.powerBIReportGroupId || item.powerBIReportGroupId)) {
    next.workspaceId = item.reportGroup?.powerBIReportGroupId || item.powerBIReportGroupId;
  }

  (config.dataFilters || []).forEach((filter) => {
    if (!filter) return;
    const dim = matchDataFilterDimension(filter.dimension);
    if (!dim) return;
    next[dim.enabled] = true;
    if (filter.table) next[dim.table] = filter.table;
    if (filter.field) next[dim.field] = filter.field;
    if (filter.fieldInternal) next[dim.fieldInternal] = filter.fieldInternal;
    if (dim.dimension === ReportDataFilterDimension.DynamicEntity) {
      const entityId = unwrapEntityId(filter.dynamicEntityId) || unwrapEntityId(filter.dynamicEntity);
      next.reportDataFilterDynamicEntityId = entityId;
      if (filter.dynamicEntity && typeof filter.dynamicEntity === 'object') {
        next.reportDataFilterDynamicEntity = filter.dynamicEntity;
      }
      next.reportDataFilterDynamicEntityFieldUserFieldId = unwrapEntityId(
        filter.dynamicEntityFieldUserFieldId,
      );
      next.reportDataFilterDynamicEntityFieldDataFieldId = unwrapEntityId(
        filter.dynamicEntityFieldDataFieldId,
      );
    }
  });

  return next;
};

/** Merge identity + ConfigPowerBIReport so consume/embed see the same filters as the form. */
export const hydrateReportForRuntime = (item, powerBIConfig) => {
  if (!item) return item;
  let next = { ...item };
  const config = powerBIConfig || next.configurations;
  if (config && isPowerBIProvider(next.provider)) {
    next = applyPowerBIConfigToForm(next, config);
  }
  return { ...next, ...flattenAccessControl(next.accessControl) };
};

/** GET `/report/config/powerBI/{id}` envelope (or `{ data: envelope }`). */
export const unwrapPowerBIConfigResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const nested = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  if (nested.report || nested.configurations || nested.serverConfiguration) {
    return nested;
  }
  if (
    nested.data &&
    typeof nested.data === 'object' &&
    (nested.data.report || nested.data.configurations || nested.data.serverConfiguration)
  ) {
    return nested.data;
  }
  return nested;
};

/** GET `/report/config/powerBI/{id}` → form/detail item (`Report` + `ConfigPowerBIReport` + server). */
export const hydratePowerBIReportItem = (reportId, raw) => {
  const src = unwrapPowerBIConfigResponse(raw);
  const report = src.report && typeof src.report === 'object' ? src.report : {};
  const hasEnvelope = !!(src.report || src.configurations || src.serverConfiguration);
  const configurations =
    src.configurations && typeof src.configurations === 'object'
      ? src.configurations
      : hasEnvelope
        ? {}
        : src;
  const serverConfiguration = src.serverConfiguration || configurations.serverConfiguration;
  const identity = hasEnvelope ? report : src;

  const base = {
    ...identity,
    id: reportId || identity.id,
    provider: identity.provider || configurations.provider || ReportProvider.PowerBI,
    usedLegacyFallback: src.usedLegacyFallback,
  };

  return hydrateReportForRuntime(base, {
    ...configurations,
    serverConfiguration,
  });
};

const pickIdentityValue = (values, key) => {
  if (!Object.prototype.hasOwnProperty.call(values, key)) return undefined;
  const value = values[key];
  return value === '' ? undefined : value;
};

export const unwrapReportEntity = (res) => {
  if (!res || typeof res !== 'object') return res;
  if (res.id) return res;
  const nested = res.data;
  if (nested && typeof nested === 'object' && nested.id) return nested;
  return res;
};

export const unwrapReportVersions = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

export const buildReportGroupPayload = (values = {}) => {
  const payload = {
    name: pickIdentityValue(values, 'name'),
    order: pickIdentityValue(values, 'order'),
    isActive: values.isActive !== false,
    ownerUserId: unwrapEntityId(values.ownerUserId || values.owner),
    powerBIServerConfigurationId: unwrapEntityId(values.powerBIServerConfigurationId),
    powerBIReportGroupId: pickIdentityValue(values, 'powerBIReportGroupId'),
    permissionsFiltersLogicalOperator: pickIdentityValue(
      values,
      'permissionsFiltersLogicalOperator',
    ),
    accessControl: buildAccessControl(values),
  };
  if (values.id) payload.id = values.id;
  return payload;
};

export const buildReportIdentityPayload = (values = {}) => {
  const provider = isNativeProvider(values.provider)
    ? ReportProvider.Native
    : ReportProvider.PowerBI;
  const type = isNativeProvider(values.provider)
    ? ReportType.Generic
    : normalizeReportType(values.type);

  const payload = {
    provider,
    title: pickIdentityValue(values, 'title'),
    description: pickIdentityValue(values, 'description'),
    tags: pickIdentityValue(values, 'tags'),
    reportPlatform: pickIdentityValue(values, 'reportPlatform'),
    type,
    isPublic: values.isPublic === true,
    isActive: values.isActive !== false,
    reportGroupId: unwrapEntityId(values.reportGroupId),
    processId: type === ReportType.Process ? unwrapEntityId(values.processId) : undefined,
  };

  const ownerUserId = unwrapEntityId(values.ownerUserId || values.owner || values.ownerUser);
  if (ownerUserId && !isEmptyGuid(ownerUserId)) {
    payload.ownerUserId = ownerUserId;
  }

  if (provider !== ReportProvider.Native && hasAccessControlInput(values)) {
    payload.accessControl = buildAccessControl(values);
  }

  if (values.id) payload.id = values.id;

  if (provider !== ReportProvider.Native) {
    payload.powerBIServerConfigurationId = unwrapEntityId(values.powerBIServerConfigurationId);
    payload.powerBIReportId = pickIdentityValue(values, 'powerBIReportId') || pickIdentityValue(values, 'reportId');
    payload.powerBIDatasetId = pickIdentityValue(values, 'powerBIDatasetId') || pickIdentityValue(values, 'datasetId');
    payload.powerBIDefaultReportSection =
      pickIdentityValue(values, 'powerBIDefaultReportSection') ||
      pickIdentityValue(values, 'defaultReportSection');
    payload.isMultipage = !!values.isMultipage;
    payload.permissionsFiltersLogicalOperator = pickIdentityValue(
      values,
      'permissionsFiltersLogicalOperator',
    );
  }

  return payload;
};
